import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Bell, Check, Clock, AlertCircle, UserPlus, UserMinus, CheckCircle, Mail, MessageSquare, Settings, Timer, Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Notification } from '../../types';
import { notificationApi } from '../../services/api';
import { useNotificationState, dispatchNotificationRefresh, type NotificationBadges } from '../../contexts/NotificationStateContext';
import { showToast } from './Toast';

const NOTIFICATIONS_PAGE_SIZE = 20;

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onMarkAsRead }) => {
  const { t } = useTranslation();
  const getIcon = () => {
    switch (notification.type) {
      case 'TASK_REMINDER':
      case 'TASK_DUE':
        return <AlertCircle className="w-4 h-4 text-blue-500" />;
      case 'TASK_ASSIGNED':
        return <UserPlus className="w-4 h-4 text-purple-500" />;
      case 'TASK_UNASSIGNED':
        return <UserMinus className="w-4 h-4 text-orange-500" />;
      case 'TASK_COMPLETED':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'PROJECT_INVITE':
        return <Mail className="w-4 h-4 text-indigo-500" />;
      case 'TASK_COMMENT':
        return <MessageSquare className="w-4 h-4 text-cyan-500" />;
      case 'REMINDER':
        return <Clock className="w-4 h-4 text-amber-500" />;
      case 'SYSTEM':
        return <Settings className="w-4 h-4 text-gray-500" />;
      case 'TIME_ENTRY_ALERT':
        return <Timer className="w-4 h-4 text-red-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatTime = (date: string) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t('notifications.time.justNow');
    if (minutes < 60) return t('notifications.time.minutesAgo', { count: minutes });
    if (hours < 24) return t('notifications.time.hoursAgo', { count: hours });
    return t('notifications.time.daysAgo', { count: days });
  };

  return (
    <div className={`p-3 border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${
      notification.status === 'PENDING' ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
    }`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{getIcon()}</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {notification.title}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
            {notification.message}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 whitespace-nowrap">
            {notification.organizationName && (
              <span className="inline-flex items-center gap-1 mr-1">
                <Building2 className="w-3 h-3" />{notification.organizationName}
              </span>
            )}
            {formatTime(notification.scheduledFor || notification.createdAt)}
          </p>
        </div>
        {notification.status === 'PENDING' && (
          <button
            onClick={() => onMarkAsRead(notification.id)}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 min-h-[44px] min-w-[44px] flex items-center justify-center"
            title={t('notifications.markAsRead')}
          >
            <Check className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

interface NotificationsDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [_totalCount, setTotalCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Use unified notification state context - single source for both bell and badges
  const { state, updateState } = useNotificationState();
  const { unreadCount } = state;

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const response = await notificationApi.list({ limit: NOTIFICATIONS_PAGE_SIZE, page: 1 });
      if (response.success && response.data) {
        const data = response.data as Notification[];
        const total = (response.meta as { total?: number })?.total ?? 0;
        setNotifications(data);
        setTotalCount(total);
        setCurrentPage(1);
        setHasMore(data.length < total);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      showToast(t('notifications.error.failedToLoadDropdown'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const response = await notificationApi.list({ limit: NOTIFICATIONS_PAGE_SIZE, page: nextPage });
      if (response.success && response.data) {
        const newNotifications = response.data as Notification[];
        const total = (response.meta as { total?: number })?.total ?? 0;
        setNotifications(prev => [...prev, ...newNotifications]);
        setTotalCount(total);
        setCurrentPage(nextPage);
        setHasMore(notifications.length + newNotifications.length < total);
      }
    } catch (error) {
      console.error('Failed to load more notifications:', error);
      showToast(t('notifications.error.failedToLoadMoreDropdown'), 'error');
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, currentPage, notifications.length]);

  const handleMarkAsRead = async (id: string) => {
    try {
      const response = await notificationApi.markAsRead(id);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, status: 'SENT' as const } : n)
      );
      
      // Update unread count from API response for immediate bell update
      if (response.success && response.data) {
        const data = response.data as { unreadCount?: number };
        if (typeof data.unreadCount === 'number') {
          updateState({ unreadCount: data.unreadCount });
        } else {
          updateState({ unreadCount: Math.max(0, unreadCount - 1) });
        }
      }
      
      // Dispatch global refresh to trigger badge refetch
      dispatchNotificationRefresh();
    } catch (error) {
      console.error('Failed to mark as read:', error);
      showToast(t('notifications.error.failedToMarkAsReadDropdown'), 'error');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, status: 'SENT' as const })));
      
      // Update both unreadCount AND badges from single API response
      // This is the key fix: ONE call updates BOTH bell and sidebar
      if (response.success && response.data) {
        const data = response.data as { unreadCount?: number; badges?: NotificationBadges };
        updateState({
          unreadCount: data.unreadCount ?? 0,
          badges: data.badges,
        });
      } else {
        updateState({ unreadCount: 0 });
      }
      
      // Dispatch global refresh for any other listeners
      dispatchNotificationRefresh();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      showToast(t('notifications.error.failedToMarkAllAsReadDropdown'), 'error');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-80 max-w-[320px] bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 z-50"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <h3 className="font-medium text-gray-900 dark:text-gray-100">{t('notifications.title')}</h3>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
          >
            {t('notifications.markAllAsRead')}
          </button>
        )}
      </div>

      <div className="max-h-[50vh] sm:max-h-[70vh] overflow-y-auto">
        {isLoading ? (
          <div className="p-4 text-center text-gray-500">{t('notifications.loading')}</div>
        ) : notifications.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p className="text-sm">{t('notifications.empty')}</p>
          </div>
        ) : (
          notifications.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkAsRead={handleMarkAsRead}
            />
          ))
        )}
      </div>

      {notifications.length > 0 && (
        <div className="p-2 border-t border-gray-200 dark:border-gray-800">
          {hasMore ? (
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="w-full text-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore ? t('notifications.loading') : t('notifications.loadMore')}
            </button>
          ) : (
            <p className="w-full text-center text-xs text-gray-400 dark:text-gray-500 py-1">
              {t('notifications.allLoaded')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationsDropdown;

// Re-export types for consumers
export type { NotificationBadges } from '../../contexts/NotificationStateContext';
